import React, { useCallback, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { theme } from 'styled-tools'
import { useLocation } from 'react-router-dom'
import { compose } from 'recompose'
import useTranslation from '../../../utils/translation'
import { extractUrlSettings } from '../../../utils/cloudinary'
import VideoWithOverlay from '../video-with-overlay/VideoWithOverlay'
import settings from '../../components/settings'
import { TextareaQuestion } from './questions/TextareaQuestion'
import { SliderQuestion } from './questions/SliderQuestion'
import { FormQuestion } from './questions/FormQuestion'
import { UploadQuestion } from './questions/UploadQuestion'
import { SelectQuestion } from './questions/SelectQuestion'
import withSplash from '../with-splash/withSplash'

const QuestionBlockItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin: ${theme('quickQuestions.container.mobile.margin')};
  padding: ${theme('quickQuestions.container.mobile.padding')};
  border: 1px solid transparent;
  border-bottom: ${theme('quickQuestions.container.bottomBorder')};

  ${(props) =>
    props.theme.quickQuestions.container.borderRadius &&
    css`
      border-radius: ${theme('quickQuestions.container.borderRadius')};
    `}

  ${(props) =>
    props.questionStyle === 'style2' &&
    `
    border-bottom: 0;
  `}

  ${(props) => props.theme.media.min.lg`
    flex-direction: ${theme('quickQuestions.container.desktop.flexDirection')};
  `}
  ${(props) => props.theme.media.min.sm`
    margin: ${theme('quickQuestions.container.desktop.margin')};
    padding: ${theme('quickQuestions.container.desktop.padding')};
  `}
  ${(props) =>
    props.requiresAnswer &&
    `
    border: 1px solid #ec8888;
    background-color: #fdf5f5;
  `}
  ${(props) =>
    props.theme.quickQuestions.container.backgroundColor &&
    css`
      background-color: ${theme('quickQuestions.container.backgroundColor')};
    `}
`

const QuestionTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: ${theme(`questionBlock.container.flexBasis`)};
  ${(props) => (props.hasOptions ? `flex-grow:0;` : `flex-grow:1;`)}
`

const QuestionHeading = styled.div`
  display: flex;
  color: ${(props) =>
    theme(`quickQuestions.heading.${props.questionStyle}.color`)};
  font-weight: ${(props) =>
    theme(`quickQuestions.heading.${props.questionStyle}.fontWeight`)};
  letter-spacing: ${(props) =>
    theme(`quickQuestions.heading.${props.questionStyle}.letterSpacing`)};
  font-family: ${(props) =>
    theme(`quickQuestions.heading.${props.questionStyle}.fontFamily`)};
  font-size: ${(props) =>
    theme(`quickQuestions.heading.${props.questionStyle}.fontSize.mobile`)};
  line-height: ${(props) =>
    theme(`quickQuestions.heading.${props.questionStyle}.lineHeight.mobile`)};
  margin: ${(props) =>
    theme(`quickQuestions.heading.${props.questionStyle}.margin.mobile`)};
  text-transform: ${(props) =>
    theme(`quickQuestions.heading.${props.questionStyle}.textTransform`)};
  padding: ${(props) =>
    theme(`quickQuestions.heading.${props.questionStyle}.padding`)};

  ${(props) => props.theme.media.min.sm`
    font-size: ${theme(
      `quickQuestions.heading.${props.questionStyle}.fontSize.desktop`
    )};
    line-height: ${theme(
      `quickQuestions.heading.${props.questionStyle}.lineHeight.desktop`
    )};
    margin: ${theme(
      `quickQuestions.heading.${props.questionStyle}.margin.desktop`
    )};
  `}
`

const QuestionPreamble = styled.div`
  display: flex;
  flex-direction: column;
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: ${theme('quickQuestions.preamble.fontSize.mobile')};
  font-weight: ${(props) =>
    theme('quickQuestions.preamble.fontWeight')(props) || '300'};
  line-height: ${theme('quickQuestions.preamble.lineHeight')};
  margin: 8px 0px 8px 0px;
  color: ${theme(`quickQuestions.preamble.color`)};
  margin-top: 0;

  a {
    color: ${theme(`colors.link`)};
  }

  p {
    padding-top: 0;
    margin-top: 0;
    font-size: ${theme('quickQuestions.preamble.fontSize.mobile')};
  }

  ul {
    padding: 0;
    margin: 0;
    margin-bottom: 14px;
    margin-top: 0;
  }
  ul > li {
    font-weight: 700;
    list-style-type: disc;
    list-style-position: inside;
    font-size: ${theme('quickQuestions.preamble.fontSize.mobile')};
  }

  strong {
    font-weight: 700;
  }

  ${(props) => props.theme.media.min.sm`
    padding-right: 30px;
    font-size: ${theme('quickQuestions.preamble.fontSize.desktop')};

    p {
      font-size: ${theme('quickQuestions.preamble.fontSize.desktop')};
    }

    ul > li {
      font-size: ${theme('quickQuestions.preamble.fontSize.desktop')};
    }
  `};
`

const QuestionImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-basis: 100%;
  ${(props) => props.theme.media.min.md`
    flex-basis: 30%;
  `};
`

const QuestionImageWidthTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  align-items: center;
  flex-basis: 100%;
  margin-top: 20px;
  margin-bottom: 20px;
  ${(props) => props.theme.media.min.md`
    flex-basis: 30%;
  `};
  ${(props) => props.theme.media.min.lg`
    margin-top: 40px;
    margin-bottom: 0px;
  `};
`

const QuestionImage = styled.img`
  display: inline;
  height: auto;
  width: auto;
  max-width: 100%;
  margin: 10px 0;
`

const QuestionVideo = styled.video`
  display: inline;
  height: auto;
  width: auto;
  margin: 10px 0;
  max-width: ${(props) =>
    theme('quickQuestions.measurementsBlock.video.maxWidth')(props) || '100%'};
  border-radius: ${(props) =>
    theme('quickQuestions.measurementsBlock.video.borderRadius')(props)};
`

const TextAndImageContainer = styled.div`
  display: ${(props) =>
    theme('questionBlock.textAndImageContainer.display')(props) || 'flex'};
  flex-direction: column;
  justify-content: space-between;

  ${(props) => props.theme.media.min.sm`
    flex-direction: row;
  `};
`

const HeadingAndPreambleContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${(props) =>
    theme(`questionBlock.heading.container.${props.questionStyle}.margin`)};
  flex-basis: 100%;
  ${(props) => props.theme.media.min.md`
    flex-basis: ${theme(
      `questionBlock.heading.container.${props.questionStyle}.flexBasis`
    )};
  `};
`

const QuestionValidation = styled.div`
  color: #ec8888;
  font-weight: 700;
  font-family: ${theme('typography.paragraph.fontFamily')};
  margin: 10px 0px;
`

const MostImportantLabel = styled.div`
  background-color: #192228;
  color: #ffffff;
  font-size: 11px;
  text-transform: uppercase;
  line-height: 14px;
  font-weight: 500;
  border-radius: 50px;
  padding: 7px 20px;
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  ${(props) => props.theme.media.min.sm`
    left: 110px;
  `}
`

const QuestionImageWrapperUploadBlock = styled.div`
  img {
    max-width: 200px;
    border-radius: 5px;
    width: 100%;
  }
`

const QuestionBlock = ({ question, answers, onUpdate, onLoading, splash, useShowHideQuestionFeature }) => {
  const t = useTranslation('app.prep-form')
  const location = useLocation()
  const questionType = useMemo(
    () => question.optionType || '',
    [question.optionType]
  )

  const update = useCallback(
    (value) => {
      onUpdate(value)
      splash()
    },
    [onUpdate, splash]
  )

  const formattedId = useMemo(
    () =>
      `block_${(question.questionText || 'default')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/gi, '')
        .replace(/\s+/g, '_')}`,
    [question.questionText]
  )

  const focused = useMemo(() => {
    const focusValue = new URLSearchParams(location.search).get('block-focus')
    if (!focusValue) {
      return true
    }

    return focusValue === formattedId
  }, [location, formattedId])

  const questionStyle = (question.questionStyle || 'style-1').replace('-', '')

  const mediaUrl = useMemo(() => {
    if (!question.questionImage?.url) {
      return null
    }
    const url = question.questionImage.url
    const publicId = question.questionImage.publicId
    if (!publicId) {
      return url
    }

    let type = question.questionImage.type
    let resourceType = question.questionImage.resourceType
    if (!type || !resourceType) {
      const { deliveryType, assetType } = extractUrlSettings(url)

      type = type || deliveryType
      resourceType = resourceType || assetType
    }

    const width = resourceType === 'video' ? 'w_800' : 'w_480'

    return `https://assets.nobiadigital.com/${resourceType}/${type}/c_scale,${width}/q_auto/f_auto/${publicId}`
  }, [question.questionImage])

  const hide = !!question.hideOnPrepFormPage
  if (hide && useShowHideQuestionFeature) {
    return null
  }

  if (!questionType || !focused) {
    return null
  }

  return (
    <QuestionBlockItem
      data-track={`section-${question.sortOrder}`}
      data-cy="question-block"
      questionStyle={questionStyle}
      requiresAnswer={question.required && question.notAnswered}
      id={formattedId}
    >
      {question.questionStyle === 'style-3' && settings.brand === 'magnet' && (
        <MostImportantLabel>{t('most-important')}</MostImportantLabel>
      )}
      <QuestionTextWrapper
        hasOptions={questionType === 'single' || questionType === 'multi'}
      >
        <TextAndImageContainer>
          <HeadingAndPreambleContainer questionStyle={questionStyle}>
            {question.questionText && (
              <QuestionHeading
                questionStyle={questionStyle}
                dangerouslySetInnerHTML={{ __html: question.questionText }}
              />
            )}
            {question.questionDescription && (
              <QuestionPreamble
                dangerouslySetInnerHTML={{
                  __html: question.questionDescription,
                }}
              />
            )}
            {question.required &&
              question.notAnswered &&
              question.requiredText && (
                <QuestionValidation>{question.requiredText}</QuestionValidation>
              )}
            {!!mediaUrl && settings.brand !== 'magnet' && (
              <QuestionImageWrapper>
                {question.questionImage.resourceType === 'video' ? (
                  <QuestionVideo
                    controls
                    playsInline
                    poster={`${mediaUrl}.jpg`}
                  >
                    <source type="video/webm" src={`${mediaUrl}.webm`} />
                    <source type="video/mp4" src={`${mediaUrl}.mp4`} />
                  </QuestionVideo>
                ) : (
                  <QuestionImage src={mediaUrl} />
                )}
              </QuestionImageWrapper>
            )}
            {!!mediaUrl && settings.brand === 'magnet' && (
              <QuestionImageWidthTextWrapper>
                {question.questionImage.resourceType === 'video' ? (
                  <VideoWithOverlay mediaUrl={mediaUrl} />
                ) : (
                  <QuestionImageWrapperUploadBlock>
                    <QuestionImage src={mediaUrl} />
                  </QuestionImageWrapperUploadBlock>
                )}
                <div>
                  <QuestionPreamble>
                    {question.questionImage.resourceType === 'video'
                      ? t('video')
                      : ''}
                  </QuestionPreamble>
                  <QuestionPreamble style={{ fontWeight: '500' }}>
                    {question.questionSupportingText}
                  </QuestionPreamble>
                </div>
              </QuestionImageWidthTextWrapper>
            )}
          </HeadingAndPreambleContainer>
        </TextAndImageContainer>
      </QuestionTextWrapper>
      {(questionType === 'single' || questionType === 'multi') && (
        <SelectQuestion
          question={question}
          answers={answers}
          onChange={update}
        />
      )}
      {questionType === 'textarea' && (
        <TextareaQuestion
          question={question}
          answers={answers}
          onChange={update}
        />
      )}
      {(questionType === 'form' || questionType === 'form-checkbox') && (
        <FormQuestion question={question} answers={answers} onChange={update} />
      )}
      {(questionType === 'uploadphotos' || questionType === 'uploadsketch') && (
        <UploadQuestion
          question={question}
          answers={answers}
          onChange={update}
          onLoading={onLoading}
          qrBlockId={formattedId}
        />
      )}

      {questionType === 'slider' && (
        <SliderQuestion
          question={question}
          answers={answers}
          onChange={update}
        />
      )}
    </QuestionBlockItem>
  )
}

export default compose(withSplash)(QuestionBlock)
